import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useCotizacionEstandarStore } from ".";
import useUtil from "@/composables/useUtil";

const useCotizacionEstandarDatosStore = defineStore("cotEstDatos", () => {
  const { formatoMoneda } = useUtil();
  const { state, stateCotizacion, tablaTIR } = storeToRefs(
    useCotizacionEstandarStore()
  );

  const valorDolar = ref(19.6932);
  const valorEuro = ref(21.3);
  const tdcUSD = ref(valorDolar.value * 1.06);
  const tdcUSDvsEUR = ref(1.1);
  const tdcEurosVentanilla = ref(valorEuro.value * 1.04);
  const mismaMoneda = ref(1);

  const rangosOperacion = {
    mayorUSD: 200000 * 1.16 * 0.9,
    normalUSD: 80000 * 1.16 * 0.9,
    menorUSD: 35000 * 1.16 * 0.9,
    mayorMXN: 200000 * tdcUSD.value * 1.16 * 0.9,
    normalMXN: 80000 * tdcUSD.value * 1.16 * 0.9,
    menorMXN: 35000 * tdcUSD.value * 1.16 * 0.9,
  };

  const tdcInformativo = computed(() => {
    let tdcInformativo = ref(0);

    if (state.value.monedaOperacion === "USD") {
      if (state.value.monedaEquipo === "USD") {
        tdcInformativo.value = 1;
      } else if (state.value.monedaEquipo === "MXN") {
        tdcInformativo.value = tdcUSD.value;
      } else {
        tdcInformativo.value = tdcUSDvsEUR.value;
      }
    } else {
      if (state.value.monedaEquipo === "MXN") {
        tdcInformativo.value = 1;
      } else if (state.value.monedaEquipo === "USD") {
        tdcInformativo.value = tdcUSD.value;
      } else {
        tdcInformativo.value = tdcUSDvsEUR.value;
      }
    }

    return tdcInformativo.value;
  });

  const precioFinal = computed(() => {
    if (state.value.monedaOperacion === "USD") {
      if (state.value.monedaEquipo === "MXN") {
        return state.value.valorEquipo / tdcInformativo.value;
      } else {
        return state.value.valorEquipo * tdcInformativo.value;
      }
    } else {
      return state.value.valorEquipo * tdcInformativo.value;
    }
  });

  const anticipoNormal = ref(0.1);
  const montoFinanciar = computed(
    () => precioFinal.value * (1 - anticipoNormal.value)
  );
  const montoFinanciarIVA = computed(() => montoFinanciar.value * 1.16);

  const rangoOperacionLabel = computed(() => {
    if (state.value.monedaOperacion === "USD") {
      if (montoFinanciarIVA.value < rangosOperacion.menorUSD) {
        //return "Monto Financiar Menor";
        return "Menor";
      }
      if (montoFinanciarIVA.value < rangosOperacion.normalUSD) {
        //return rangosOperacion.menorUSD;
        return "Normal";
      }
      if (montoFinanciarIVA.value < rangosOperacion.mayorUSD) {
        //return rangosOperacion.normalUSD;
        return "Mayor";
      }

      return "Mayor";
    } else {
      //
      if (montoFinanciarIVA.value < rangosOperacion.menorMXN) {
        //return "Monto Financiar Menor";
        return "Menor";
      }
      if (montoFinanciarIVA.value < rangosOperacion.normalMXN) {
        //return rangosOperacion.menorMXN;
        //return "Normal";
        return "Menor";
      }
      /* if (montoFinanciarIVA.value < rangosOperacion.mayorMXN) {
        //return rangosOperacion.normalMXN;
        return "Mayor";
      } */

      return "Mayor";
    }
  });

  const datosGenerales = computed(() => {
    return {
      tdcInformativo: tdcInformativo.value,
      precioFinal: precioFinal.value,
      rangoOperacionLabel: rangoOperacionLabel.value,
      anticipoNormal: anticipoNormal.value,
      montoFinanciar: montoFinanciar.value,
      montoFinanciarIVA: montoFinanciarIVA.value,
    };
  });

  const obtenerDatoPorCodigo = computed(() => {
    const codigo = `${stateCotizacion.value.tipo}${state.value.monedaOperacion}${datosGenerales.value.rangoOperacionLabel}${stateCotizacion.value.plazo}`;

    return codigo;
  });

  const obtenerDatoPorCodigoPlazo = (plazo) => {
    const codigo = `${stateCotizacion.value.tipo}${state.value.monedaOperacion}${datosGenerales.value.rangoOperacionLabel}${plazo}`;

    return codigo;
  };

  const datoSeleccionado = computed(() => {
    if (tablaTIR.value.length === 0) return undefined;

    const dato = tablaTIR.value.filter(
      (row) => obtenerDatoPorCodigo.value == row.etiqueta
    );

    if (!dato[0]) return undefined;

    return dato[0];
  });

  const obtenerDatoPorPlazo = (plazo, concepto, esPorcentaje = true) => {
    if (tablaTIR.value.length === 0) return undefined;

    const etiqueta = `${stateCotizacion.value.tipo}${state.value.monedaOperacion}${datosGenerales.value.rangoOperacionLabel}${plazo}`;

    const dato = tablaTIR.value.filter((row) => etiqueta == row.etiqueta);

    if (!dato[0]) return "NaN";

    return esPorcentaje
      ? parseFloat(dato[0][concepto]) * 100
      : dato[0][concepto];
  };

  const calculaReferencia = (concepto, esPorcentaje = true) => {
    console.log(concepto, esPorcentaje);
    return esPorcentaje
      ? parseFloat(datoSeleccionado.value[concepto]) * 100
      : datoSeleccionado.value[concepto];
  };

  const calculaEnganche = (plazo) => {
    let enganche = obtenerDatoPorPlazo(plazo, "anticipo", false);
    enganche = precioFinal.value * enganche;
    const valor = `enganche_${plazo}`;
    stateCotizacion.value[valor] = enganche;
    return parseFloat(enganche);
  };

  const calculaMontoFinanciar = (plazo) => {
    const valor = `enganche_${plazo}`;
    const montoFinanciar = precioFinal.value - stateCotizacion.value[valor];
    stateCotizacion.value[`montoFinanciar_${plazo}`] = montoFinanciar;

    return montoFinanciar;
  };
  const calculaValorResidualCalculo = (plazo) => {
    let residualCalculo = 0;
    if (state.value.tipoArrendamiento == "puro") {
      residualCalculo = obtenerDatoPorPlazo(plazo, "residual_calculo", false);

      residualCalculo = precioFinal.value * residualCalculo;
      stateCotizacion.value[`residualCalculo_${plazo}`] = residualCalculo;
      return residualCalculo;
    } else {
      // TODO: Revisar formula!!
      residualCalculo = obtenerDatoPorPlazo(
        plazo,
        "opcion_compra_oculta",
        false
      );
      residualCalculo = precioFinal.value * residualCalculo;
      stateCotizacion.value[`residualCalculo_${plazo}`] = residualCalculo;
      return residualCalculo;
    }
  };

  function calculaPmt(rate, nper, pv, fv = 0, type = 0) {
    if (rate === 0) {
      return -(pv + fv) / nper;
    } else {
      const r = rate; // Tasa de interés por período
      return (
        -((pv + fv / Math.pow(1 + r, nper)) * r) / (1 - Math.pow(1 + r, -nper))
      );
    }
  }

  const calculaRentaMensual = (plazo) => {
    const tasaInteres = obtenerDatoPorPlazo(plazo, "tasa", false);
    const nper = plazo;
    const rate = tasaInteres / 12;
    const pv = -stateCotizacion.value[`montoFinanciar_${plazo}`];
    const fv = stateCotizacion.value[`residualCalculo_${plazo}`];

    const pmt = calculaPmt(rate, nper, pv, fv, 0);

    stateCotizacion.value[`rentaMensual_${plazo}`] = pmt;

    return parseFloat(pmt.toFixed(2));
  };

  const calculaRelacionRentaEquipo = (plazo) => {
    const relacionRentaEquipo =
      stateCotizacion.value[`rentaMensual_${plazo}`] / precioFinal.value;
    stateCotizacion.value[`relacionRentaEquipo_${plazo}`] = relacionRentaEquipo;

    return relacionRentaEquipo.toFixed(4);
  };

  const calculaComision = (plazo) => {
    const comision =
      precioFinal.value * obtenerDatoPorPlazo(plazo, "comision", false);
    stateCotizacion.value[`comision_${plazo}`] = comision;

    return parseFloat(comision.toFixed(2));
  };

  const calculaSubtotal = (plazo) => {
    const subtotal =
      stateCotizacion.value[`enganche_${plazo}`] +
      stateCotizacion.value[`comision_${plazo}`];
    stateCotizacion.value[`subtotal_${plazo}`] = subtotal;
    return subtotal.toFixed(2);
  };

  const calculaIva = (plazo) => {
    const iva = stateCotizacion.value[`subtotal_${plazo}`] * 0.16;
    stateCotizacion.value[`iva_${plazo}`] = iva;

    return iva.toFixed(2);
  };

  const calculaRentaEnDeposito = (plazo) => {
    const rentaDeposito =
      stateCotizacion.value[`rentaMensual_${plazo}`] *
      obtenerDatoPorPlazo(plazo, "ultima_renta", false);

    stateCotizacion.value[`rentaDeposito_${plazo}`] = rentaDeposito;
    return parseFloat(rentaDeposito.toFixed(2));
  };

  const calculaTotalPagoInicial = (plazo) => {
    const totalPagoInicial =
      stateCotizacion.value[`subtotal_${plazo}`] +
      stateCotizacion.value[`iva_${plazo}`] +
      stateCotizacion.value[`rentaDeposito_${plazo}`];
    stateCotizacion.value[`totalPagoInicial_${plazo}`] = totalPagoInicial;
    return totalPagoInicial.toFixed(2);
  };

  const calculaIvaRenta = (plazo) => {
    const ivaRenta = stateCotizacion.value[`rentaMensual_${plazo}`] * 0.16;
    stateCotizacion.value[`ivaRenta_${plazo}`] = ivaRenta;

    return ivaRenta.toFixed(2);
  };

  const calculaTotalRenta = (plazo) => {
    const totalRenta =
      stateCotizacion.value[`ivaRenta_${plazo}`] +
      stateCotizacion.value[`rentaMensual_${plazo}`];
    stateCotizacion.value[`totalRenta_${plazo}`] = totalRenta;

    return totalRenta;
  };

  return {
    anticipoNormal,
    calculaComision,
    calculaEnganche,
    calculaIva,
    calculaIvaRenta,
    calculaMontoFinanciar,
    calculaPmt,
    calculaReferencia,
    calculaRelacionRentaEquipo,
    calculaRentaEnDeposito,
    calculaRentaMensual,
    calculaSubtotal,
    calculaTotalPagoInicial,
    calculaTotalRenta,
    calculaValorResidualCalculo,
    mismaMoneda,
    montoFinanciar,
    montoFinanciarIVA,
    obtenerDatoPorCodigo,
    obtenerDatoPorCodigoPlazo,
    obtenerDatoPorPlazo,
    precioFinal,
    rangoOperacionLabel,
    rangosOperacion,
    tdcEurosVentanilla,
    tdcInformativo,
    tdcUSD,
    tdcUSDvsEUR,
    valorDolar,
    valorEuro,
  };
});

export default useCotizacionEstandarDatosStore;
